import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Settings2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import CareWeeklyCalendar from "./CareWeeklyCalendar";

const defaultSettings = {
  upload_weekday: 4, upload_time: "18:00", attention_delay_hours: 12,
  explicit_upload_requested: false,
};

// Older fixed plans omitted the end time; their existing projection lasts 90 minutes.
const fixedPlanSettings = (settings = {}) => ({
  ...defaultSettings, ...settings,
  fixed_weekly_sessions: (settings.fixed_weekly_sessions || []).map((row, index) => {
    const [hours, minutes] = (row.start_time || "").split(":").map(Number);
    const end = hours * 60 + minutes + 90;
    return {
      ...row, id: row.id || `fixed-${index}`,
      end_time: row.end_time || (Number.isFinite(end) && end < 1440
        ? `${String(Math.floor(end / 60)).padStart(2, "0")}:${String(end % 60).padStart(2, "0")}` : ""),
    };
  }),
});

const validFixedSession = (row) => Number.isInteger(row.weekday) && row.weekday >= 0 && row.weekday <= 6
  && /^([01]\d|2[0-3]):[0-5]\d$/.test(row.start_time)
  && /^([01]\d|2[0-3]):[0-5]\d$/.test(row.end_time)
  && row.end_time > row.start_time;

const newEntry = (week) => ({
  id: null, _key: `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  local_date: week, start_time: "", unit_type: "team_training",
  title: "", description: "", duration_minutes: 90,
  fixture_schedule_id: null, opponent: null,
  review_origin: "manual", review_status: "accepted",
});

const entryKey = (entry, index) => entry.id || entry._key || `entry-${index}`;

function reviewEntries(rows) {
  return (rows || []).map((row, index) => ({
    ...row, unit_type: row.unit_type ?? "team_training", _key: entryKey(row, index),
  }));
}

function reviewPayload(entries) {
  return entries.map(({ _key, fixture_match, ...entry }) => ({
    ...entry, id: entry.id || null, duration_minutes: Number(entry.duration_minutes),
  }));
}

export const reviewSummary = (entries) => (entries || []).reduce((summary, entry) => {
  if (!["ai", "club_proposal"].includes(entry.review_origin)) return summary;
  const status = entry.review_status || "pending";
  if (Object.prototype.hasOwnProperty.call(summary, status)) summary[status] += 1;
  return summary;
}, { pending: 0, accepted: 0, rejected: 0 });

export const sortTrainingPlansForWeek = (left, right) => {
  const reviewPriority = Number(right.status !== "confirmed") - Number(left.status !== "confirmed");
  if (reviewPriority) return reviewPriority;
  const versionPriority = Number(right.version || 0) - Number(left.version || 0);
  if (versionPriority) return versionPriority;
  return Number(right.plan_kind === "club_proposal") - Number(left.plan_kind === "club_proposal");
};

export function CareTrainingPlanSettings({ playerId }) {
  const { t, lang } = useI18n();
  const [settings, setSettings] = useState(defaultSettings);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState({ text: "", error: false });
  const [loaded, setLoaded] = useState(false);
  const fixedPlanError = lang === "de"
    ? "Bitte wähle für jedes Training einen Wochentag sowie Start- und Endzeit. Die Endzeit muss nach der Startzeit liegen."
    : "Choose a weekday, start and end time for each training session. The end time must be after the start time.";

  const load = useCallback(async () => {
    setLoaded(false);
    try {
      const response = await api.get(`/care/players/${playerId}/training-plans`);
      setSettings(fixedPlanSettings(response.data.settings));
      setLoaded(true);
    } catch (err) {
      setMessage({ text: apiError(err.response?.data?.detail), error: true });
    }
  }, [playerId]);

  useEffect(() => { load(); }, [load]);

  const updateFixedSession = (index, field, value) => setSettings((current) => ({
    ...current,
    fixed_weekly_sessions: (current.fixed_weekly_sessions || []).map((row, rowIndex) => (
      rowIndex === index ? { ...row, [field]: value } : row
    )),
  }));

  const save = async (requestPlan = false) => {
    if (settings.mode === "fixed" && !(settings.fixed_weekly_sessions || []).every(validFixedSession)) {
      setMessage({ text: fixedPlanError, error: true });
      return;
    }
    setBusy(requestPlan ? "request" : "save"); setMessage({ text: "", error: false });
    try {
      const response = await api.patch(`/care/players/${playerId}/training-plan-settings`, {
        ...settings, attention_delay_hours: Number(settings.attention_delay_hours),
        explicit_upload_requested: requestPlan,
      });
      setSettings(fixedPlanSettings(response.data.settings));
      setMessage({ text: t(requestPlan ? "trainingPlanRequested" : "trainingPlanSettingsSaved"), error: false });
    } catch (err) {
      setMessage({ text: settings.mode === "fixed" && err.response?.status === 422
        ? fixedPlanError : apiError(err.response?.data?.detail), error: true });
    } finally { setBusy(""); }
  };

  return <details className="pc-card group" data-testid="training-plan-settings">
    <summary className="p-4 flex items-center gap-3 cursor-pointer list-none">
      <span className="rounded-xl p-2" style={{ background: "var(--pc-cyan-dim)" }}><Settings2 size={16} color="var(--pc-cyan)" /></span>
      <span className="min-w-0"><span className="block font-display font-semibold">{t("trainingPlanSettings")}</span><span className="block text-xs mt-0.5" style={{ color: "var(--pc-muted)" }}>{settings.mode === "fixed" ? (lang === "de" ? "Fester Wochenplan · kein Upload erforderlich" : "Fixed weekly plan · no upload required") : t("planExpectedCompact").replace("{day}", t(`weekday_${settings.upload_weekday}`)).replace("{time}", settings.upload_time)}</span></span>
      <ChevronDown size={16} className="ml-auto transition-transform group-open:rotate-180" color="var(--pc-muted)" />
    </summary>
    <div className="px-4 pb-4 border-t" style={{ borderColor: "var(--pc-border)" }}>
      {settings.mode === "fixed" && <fieldset disabled={Boolean(busy)} className="rounded-xl p-3 mt-4 text-sm min-w-0" data-testid="care-fixed-training-settings" style={{ background: "var(--pc-card-inset)", border: "1px solid var(--pc-border)" }}>
        <strong>{lang === "de" ? "Fester Trainingsplan" : "Fixed training plan"}</strong>
        <p className="text-xs mt-1" style={{ color: "var(--pc-muted)" }}>{lang === "de" ? "Änderungen gelten nach dem Speichern für künftige, noch offene Trainings. Vergangene und individuell bearbeitete Einträge bleiben erhalten." : "Saved changes apply to future, untouched training sessions. Past and individually edited entries are preserved."}</p>
        <div className="space-y-3 mt-3">{(settings.fixed_weekly_sessions || []).map((row, index) => <div className="grid grid-cols-2 sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 items-end" key={row.id}>
          <label className="text-xs col-span-2 sm:col-span-1">{lang === "de" ? "Wochentag" : "Weekday"}<select aria-label={`${lang === "de" ? "Trainingstag" : "Training day"} ${index + 1}`} className="pc-input mt-1 w-full min-w-0" value={row.weekday} onChange={(event) => updateFixedSession(index, "weekday", Number(event.target.value))}>{[0, 1, 2, 3, 4, 5, 6].map((day) => <option value={day} key={day}>{t(`weekday_${day}`)}</option>)}</select></label>
          <label className="text-xs min-w-0">{lang === "de" ? "Startzeit" : "Start time"}<input aria-label={`${lang === "de" ? "Trainingsbeginn" : "Training start"} ${index + 1}`} type="time" className="pc-input mt-1 w-full min-w-0" value={row.start_time || ""} onChange={(event) => updateFixedSession(index, "start_time", event.target.value)} /></label>
          <label className="text-xs min-w-0">{lang === "de" ? "Endzeit" : "End time"}<input aria-label={`${lang === "de" ? "Trainingsende" : "Training end"} ${index + 1}`} type="time" className="pc-input mt-1 w-full min-w-0" value={row.end_time || ""} onChange={(event) => updateFixedSession(index, "end_time", event.target.value)} /></label>
          <button type="button" className="pc-btn pc-btn-ghost col-span-2 sm:col-span-1" aria-label={`${lang === "de" ? "Training löschen" : "Remove training"} ${index + 1}`} onClick={() => setSettings((current) => ({ ...current, fixed_weekly_sessions: current.fixed_weekly_sessions.filter((session) => session.id !== row.id) }))}>{lang === "de" ? "Löschen" : "Remove"}</button>
        </div>)}</div>
        <button type="button" className="pc-btn pc-btn-ghost mt-3" disabled={(settings.fixed_weekly_sessions || []).length >= 21} onClick={() => setSettings((current) => ({
          ...current, fixed_weekly_sessions: [...(current.fixed_weekly_sessions || []), {
            id: `fixed-${Date.now()}-${Math.random().toString(16).slice(2)}`, weekday: 0, start_time: "", end_time: "",
          }],
        }))}>{lang === "de" ? "Training hinzufügen" : "Add training"}</button>
      </fieldset>}
      {settings.mode !== "fixed" && <div className="grid md:grid-cols-3 gap-3 mt-4">
        <label className="text-xs">{t("expectedUploadWeekday")}<select className="pc-input mt-1" value={settings.upload_weekday} onChange={(event) => setSettings({ ...settings, upload_weekday: Number(event.target.value) })}>{[0, 1, 2, 3, 4, 5, 6].map((day) => <option value={day} key={day} label={t(`weekday_${day}`)} />)}</select></label>
        <label className="text-xs">{t("expectedUploadTime")}<input type="time" className="pc-input mt-1" value={settings.upload_time} onChange={(event) => setSettings({ ...settings, upload_time: event.target.value })} /></label>
        <label className="text-xs">{t("careHintMissingPlan")}<span className="block text-[10px] mt-1" style={{ color: "var(--pc-muted)" }}>{t("hintAfter")}</span><div className="flex items-center gap-2 mt-1"><input type="number" min="0" max="168" className="pc-input" value={settings.attention_delay_hours} onChange={(event) => setSettings({ ...settings, attention_delay_hours: event.target.value })} /><span className="text-xs" style={{ color: "var(--pc-muted)" }}>{t("hours")}</span></div></label>
      </div>}
      {message.text && <p role={message.error ? "alert" : "status"} className="text-xs mt-3" style={{ color: message.error ? "var(--pc-score-poor)" : "var(--pc-score-good)" }}>{message.text}</p>}
      <div className="flex flex-wrap gap-2 mt-4"><button type="button" className="pc-btn pc-btn-ghost" disabled={!loaded || Boolean(busy)} onClick={() => save(false)}>{t("saveSettings")}</button>{settings.mode !== "fixed" && <button type="button" className="pc-btn pc-btn-primary" disabled={!loaded || Boolean(busy)} onClick={() => save(true)}>{t("requestTrainingPlan")}</button>}</div>
    </div>
  </details>;
}

export default function CareTrainingPlanReview({
  playerId, player, onOpenServices, focusDate = "", initialPlanId = "",
  initialClubPlanId = "", initialScheduleItemId = "",
  onDateChange = null, weekNavigator = null, preparationMode = false,
}) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [visibleWeek, setVisibleWeek] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [reviewMode, setReviewMode] = useState(false);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [focusEntryKey, setFocusEntryKey] = useState("");
  const [focusToken, setFocusToken] = useState(0);
  const [shareWithClub, setShareWithClub] = useState(false);
  const initialPlanHandled = useRef(false);

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/care/players/${playerId}/training-plans`);
      setData(response.data); setError("");
      return response.data;
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
      return null;
    }
  }, [playerId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data || initialPlanHandled.current || (!initialPlanId && !initialClubPlanId)) return;
    const requested = [
      ...(data.training_plans || []), ...(data.club_training_plan_reviews || []),
    ].find((plan) => plan.id === (initialClubPlanId || initialPlanId));
    initialPlanHandled.current = true;
    if (!requested) return;
    setSelectedId(requested.id); setVisibleWeek(requested.week_start);
    setReviewMode(requested.status !== "confirmed");
  }, [data, initialClubPlanId, initialPlanId]);

  const availablePlans = useMemo(() => [
    ...(data?.training_plans || []), ...(data?.club_training_plan_reviews || []),
  ], [data]);
  const plansForWeek = useMemo(() => availablePlans.filter((plan) => (
    plan.week_start === visibleWeek
  )).sort(sortTrainingPlansForWeek), [availablePlans, visibleWeek]);
  const selected = useMemo(() => {
    const explicit = plansForWeek.find((plan) => plan.id === selectedId);
    return explicit || plansForWeek[0] || null;
  }, [plansForWeek, selectedId]);

  useEffect(() => {
    if (!selected) {
      setSelectedId(""); setEntries([]); setReviewMode(false); return;
    }
    setSelectedId(selected.id); setEntries(reviewEntries(selected.entries)); setFocusEntryKey("");
    setShareWithClub(Boolean(selected.club_share?.requested));
    if (selected.status === "confirmed") setReviewMode(false);
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!focusDate && selected?.week_start) onDateChange?.(selected.week_start);
  }, [focusDate, onDateChange, selected?.week_start]);

  const replacePlan = (updated) => {
    const collection = updated.plan_kind === "club_proposal"
      ? "club_training_plan_reviews" : "training_plans";
    setData((current) => ({
      ...current,
      [collection]: (current[collection] || []).map((plan) => (
        plan.id === updated.id ? updated : plan
      )),
    }));
    setEntries(reviewEntries(updated.entries));
  };
  const focusEntry = (entry) => {
    if (!entry) return;
    setFocusEntryKey(entryKey(entry)); setFocusToken((current) => current + 1);
  };
  const isClubProposal = selected?.plan_kind === "club_proposal";
  const pendingMessage = (count) => t(isClubProposal
    ? (count === 1 ? "clubSuggestionPendingOne" : "clubSuggestionPendingMany")
    : (count === 1 ? "trainingSuggestionPendingOne" : "trainingSuggestionPendingMany")
  ).replace("{count}", count);
  const blockPending = () => {
    const pending = entries.filter((entry) => (
      (isClubProposal ? entry.review_origin === "club_proposal" : entry.review_origin === "ai")
      && entry.review_status === "pending"
    ));
    if (!pending.length) return false;
    setError(pendingMessage(pending.length)); setNotice(""); focusEntry(pending[0]);
    return true;
  };

  const changeEntry = (key, patch) => setEntries((current) => current.map((row, index) => (
    entryKey(row, index) === key ? { ...row, ...patch } : row
  )));
  const addEntry = (entry = null) => setEntries((current) => [
    ...current, entry || newEntry(selected?.week_start || visibleWeek),
  ]);
  const removeEntry = (key) => setEntries((current) => current.filter((row, index) => (
    entryKey(row, index) !== key
  )));

  const decideSuggestion = async (key, decision) => {
    if (!selected) return;
    const entry = entries.find((row, index) => entryKey(row, index) === key);
    if (!entry) return;
    if (decision === "accepted" && (!entry.unit_type || !entry.start_time)) {
      setError(!entry.start_time ? t("trainingDraftTimeMissing") : t("calendarRequiredFields"));
      setNotice(""); focusEntry(entry); return;
    }
    setBusy(`suggestion-${key}`); setError(""); setNotice("");
    try {
      const base = isClubProposal ? "/care/club-training-plan-reviews" : "/care/training-plans";
      const response = await api.patch(`${base}/${selected.id}/suggestions/${entry.id}`, {
        decision,
        entry: decision === "accepted" ? reviewPayload([entry])[0] : null,
      });
      replacePlan(response.data.training_plan);
      setNotice(t(decision === "accepted" ? "trainingSuggestionAccepted" : "trainingSuggestionRejected"));
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const saveReview = async () => {
    if (!selected || blockPending()) return;
    const invalid = entries.find((entry) => entry.review_status !== "rejected" && (!entry.unit_type || !entry.start_time));
    if (invalid) {
      setError(!invalid.start_time ? t("trainingDraftTimeMissing") : t("calendarRequiredFields"));
      setNotice(""); focusEntry(invalid); return;
    }
    setBusy("review"); setError(""); setNotice("");
    try {
      const path = isClubProposal
        ? `/care/club-training-plan-reviews/${selected.id}`
        : `/care/training-plans/${selected.id}`;
      const response = await api.patch(path, { entries: reviewPayload(entries) });
      replacePlan(response.data.training_plan); setNotice(t("trainingPlanReviewSaved"));
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const confirm = async () => {
    if (!selected || blockPending()) return;
    const invalid = entries.find((entry) => entry.review_status !== "rejected" && (!entry.unit_type || !entry.start_time));
    if (invalid) {
      setError(!invalid.start_time ? t("trainingDraftTimeMissing") : t("calendarRequiredFields"));
      setNotice(""); focusEntry(invalid); return;
    }
    setBusy("confirm"); setError(""); setNotice("");
    try {
      const base = isClubProposal ? "/care/club-training-plan-reviews" : "/care/training-plans";
      await api.patch(`${base}/${selected.id}`, { entries: reviewPayload(entries) });
      const response = await api.post(`${base}/${selected.id}/confirm`, isClubProposal ? undefined : {
        share_with_club: shareWithClub,
      });
      replacePlan(response.data.training_plan); setReviewMode(false);
      setNotice(t(isClubProposal ? "clubTrainingPlanConfirmed" : "trainingPlanConfirmed"));
      await load();
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const shareConfirmedPlan = async () => {
    if (!selected || isClubProposal || selected.status !== "confirmed") return false;
    setBusy("share"); setError(""); setNotice("");
    try {
      const response = await api.post(`/care/training-plans/${selected.id}/confirm`, {
        share_with_club: true,
      });
      await load();
      if (!response.data.club_share?.shared) {
        setError(t("clubPlanShareNoRecipientsDetail"));
        return false;
      }
      setNotice(t("clubPlanSharedNow"));
      return true;
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
      return false;
    } finally { setBusy(""); }
  };

  const expected = data?.settings ? t("planExpectedCompact")
    .replace("{day}", t(`weekday_${data.settings.upload_weekday ?? 4}`))
    .replace("{time}", data.settings.upload_time || "18:00") : "";
  const summary = reviewSummary(entries);
  const calendarAnchor = focusDate || selected?.week_start || visibleWeek;
  const handleWeekChange = useCallback((weekStart) => {
    if (weekStart !== visibleWeek) {
      setVisibleWeek(weekStart); setReviewMode(false); setError(""); setNotice("");
    }
  }, [visibleWeek]);

  if (!data) return <section className="pc-card p-5">{error || <div className="pc-spinner mx-auto" />}</section>;
  return <section data-testid="care-training-plan-review">
    <CareWeeklyCalendar
      playerId={playerId} player={player} onOpenServices={onOpenServices}
      focusDate={calendarAnchor} onWeekChange={handleWeekChange}
      focusItemId={initialScheduleItemId}
      onFocusDateChange={onDateChange}
      weekNavigator={weekNavigator}
      preparationMode={preparationMode}
      planContext={preparationMode ? null : {
        plan: selected, versions: plansForWeek, reviewMode, summary,
        expected: data.settings?.mode === "fixed" ? "" : expected,
        fixedWeekly: data.settings?.mode === "fixed",
        busy: Boolean(busy), error, notice,
        onReview: () => { setReviewMode(true); setError(""); setNotice(""); },
        onCloseReview: () => { setReviewMode(false); setError(""); },
        originalHref: selected ? (isClubProposal
          ? `/care/club-training-plan-reviews/${selected.id}/original`
          : `/care/training-plans/${selected.id}/original`) : "",
        onSelectVersion: (id) => { setSelectedId(id); setReviewMode(false); },
        onSave: saveReview, onConfirm: confirm,
        isClubProposal,
        shareWithClub,
        onShareWithClubChange: setShareWithClub,
        onShareConfirmedPlan: shareConfirmedPlan,
        clubShare: selected?.club_share || null,
      }}
      trainingReview={reviewMode && selected ? {
        weekStart: selected.week_start, version: selected.version,
        editable: selected.status !== "confirmed", entries,
        fixtures: data.fixtures || [], busy: Boolean(busy), error, notice,
        focusEntryKey, focusToken,
        suggestionKind: isClubProposal ? "club_proposal" : "ai",
        onChangeEntry: changeEntry, onAddEntry: addEntry, onRemoveEntry: removeEntry,
        onAcceptSuggestion: (key) => decideSuggestion(key, "accepted"),
        onRejectSuggestion: (key) => decideSuggestion(key, "rejected"),
      } : null}
    />
  </section>;
}
